import * as React from "react"
import type { useSortable } from "@dnd-kit/sortable"
import { useTranslation } from "react-i18next"
import { Icon, cn } from "@nexus/ui"
import { OPPORTUNITY_DRAG_GRIP_FOCUS_CLASS } from "../../lib/opportunity-drag-reorder-grid-classes"

/** Context wired by {@link useSortable} on row/card wrappers (`group/row`). */
export const OpportunityRowDragContext = React.createContext<{
  listeners?: ReturnType<typeof useSortable>["listeners"]
  attributes?: ReturnType<typeof useSortable>["attributes"]
}>({})

/** Grip shown on hover — gutter left of row/card chrome (matches Jira). */
export function OpportunityRowDragHandle({ variant = "table" }: { variant?: "table" | "card" }) {
  const { t } = useTranslation()
  const { listeners, attributes } = React.use(OpportunityRowDragContext)
  const isCard = variant === "card"
  return (
    <button
      type="button"
      aria-label={t("common.dragToReorder")}
      className={cn(
        "absolute top-1/2 z-10 -translate-y-1/2",
        "flex items-center justify-center touch-none rounded-md",
        OPPORTUNITY_DRAG_GRIP_FOCUS_CLASS,
        "border border-transparent transition-[opacity,background-color,border-color,box-shadow,color] duration-150 motion-reduce:transition-none",
        !isCard && "hover:border-border/70 hover:bg-muted/50 hover:text-muted-foreground hover:shadow-sm",
        isCard && [
          // 1 — card hovered: faint chip + lower opacity so it stays secondary
          "group-hover/row:border-border/35 group-hover/row:bg-muted/16 group-hover/row:text-muted-foreground/50 group-hover/row:shadow-none",
          // 2 — grip hovered: clearer but toned down vs full contrast
          "hover:border-border/55 hover:bg-muted/36 hover:text-muted-foreground hover:shadow-sm hover:opacity-95",
        ],
        "text-muted-foreground/40",
        "cursor-grab active:cursor-grabbing",
        isCard ? "-left-4 size-8" : "-left-3 size-7",
        listeners && (isCard ? "opacity-0 group-hover/row:opacity-[0.78] hover:opacity-95" : "opacity-0 group-hover/row:opacity-100"),
        !listeners && "invisible pointer-events-none",
      )}
      {...(listeners ?? {})}
      {...(attributes ?? {})}
      tabIndex={listeners ? 0 : -1}
      onClick={(e) => e.stopPropagation()}
    >
      <Icon name="grip-vertical" size={isCard ? "md" : "xs"} color="inherit" />
    </button>
  )
}
